import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import api from '../api';
import KpiCard from '../components/KpiCard';
import DateRange from '../components/DateRange';

function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; }
function today() { return new Date().toISOString().split('T')[0]; }
function fmtHuf(v) { return Math.round(v).toLocaleString('hu-HU'); }

export default function Revenue() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [monthly, setMonthly] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    api.revenue.monthly(new Date().getFullYear().toString()).then(setMonthly).catch(() => {});
    api.revenue.downloads(from, to).then(setDownloads).catch(() => {});
    api.revenue.daily(from, to).then(setDaily).catch(() => {});
  }, [from, to]);

  const totalHuf = daily.reduce((s, r) => s + (r.amount_huf || 0), 0);
  const totalTx = daily.reduce((s, r) => s + r.transactions, 0);
  const iapHuf = daily.filter(r => r.source !== 'b2b_sponsor').reduce((s, r) => s + (r.amount_huf || 0), 0);
  const b2bHuf = daily.filter(r => r.source === 'b2b_sponsor').reduce((s, r) => s + (r.amount_huf || 0), 0);

  const dlChart = downloads.sort((a, b) => a.date.localeCompare(b.date));

  const monthlyByProduct = {};
  monthly.forEach(r => {
    if (!monthlyByProduct[r.month]) monthlyByProduct[r.month] = { month: r.month };
    const key = r.product || r.source;
    monthlyByProduct[r.month][key] = (monthlyByProduct[r.month][key] || 0) + (r.total_huf || 0);
  });
  const monthChart = Object.values(monthlyByProduct).sort((a, b) => a.month.localeCompare(b.month));
  const products = [...new Set(monthly.map(r => r.product || r.source))];

  // A monthly_125 a havi előfizetés (2026-10 óta helyesen besorolva; előtte
  //   „traveler" alatt szerepelt). A nyers SKU-s régi sorok címkéje marad.
  const PRODUCT_LABELS = { starter: 'Starter (15)', explorer: 'Explorer (50)', traveler: 'Traveler (125)', poi_75: 'POI 75', monthly_125: 'Előfizetés (havi 125)', 'com.aitourmate.monthly_125poi': 'Előfizetés (havi 125)' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Bevételek</h1>
        <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Össz. bevétel" value={fmtHuf(totalHuf)} suffix="Ft" />
        <KpiCard label="IAP bevétel" value={fmtHuf(iapHuf)} suffix="Ft" />
        <KpiCard label="B2B bevétel" value={fmtHuf(b2bHuf)} suffix="Ft" color="#fbbf24" />
        <KpiCard label="Tranzakciók" value={totalTx} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Havi bevétel (Ft)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="month" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} formatter={(v) => fmtHuf(v) + ' Ft'} />
              <Legend />
              {products.map((p, i) => <Bar key={p} dataKey={p} stackId="a" fill={['#00d4ff', '#34d399', '#fbbf24', '#fb923c'][i % 4]} name={PRODUCT_LABELS[p] || p} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Letöltések</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dlChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Legend />
              <Line type="monotone" dataKey="downloads" stroke="#00d4ff" name="Letöltések" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
